import * as React from 'react';
import type {
  AlignType as IAlignType,
  FittingType as IFittingType,
  UpscaleMethod,
  ImageTransformOptions,
  ImagePlaceholderTransformTarget,
  Encoding,
} from '@wix/image-kit';

export type FittingType = IFittingType;
export type AlignType = IAlignType;

type UnitLength = {
  value: number;
  type: 'px' | 'em' | 'rem' | 'vh' | 'vw' | 'vmin' | 'vmax';
};

type ImageSharpeningQuality = {
  quality?: number;
  unsharpMask?: {
    radius: number;
    amount: number;
    threshold: number;
  };
};

type ImagePlaceholderDataSrc = {
  id: string;
  width: number;
  height: number;
  crop: Crop;
  focalPoint?: FocalPoint;
  name: string;
};

export type ImageAttributesData = {
  uri?: string;
  css: {
    img: Record<string, number | string>;
    container?: Record<string, number | string>;
  };
  attr: {
    img: Record<string, number | string>;
    container: Record<string, number | string>;
  };
  transformed: boolean;
  srcset?: {
    dpr: string[];
  };
};

export type ImagePlaceholderData = {
  fittingType: FittingType;
  src: ImagePlaceholderDataSrc;
  target: ImagePlaceholderTransformTarget;
  options: Partial<ImageTransformOptions>;
};

export type PlaceholderTransition = 'blur';

export type SourceSet = {
  mediaQuery: string;
  displayMode: FittingType;
  crop?: Crop;
  focalPoint?: FocalPoint;
  scrollEffect?: 'parallax' | 'fixed' | 'ImageParallax' | 'ImageReveal';
};

export type ImageInfo = {
  containerId?: string;
  alignType?: AlignType;
  skipMeasure?: boolean;
  displayMode: FittingType;
  targetWidth?: number;
  targetHeight?: number;
  targetScale?: number;
  isLQIP?: boolean;
  isSEOBot?: boolean;
  lqipTransition?: PlaceholderTransition;
  imageData: {
    width: number;
    height: number;
    uri: string;
    name: string;
    displayMode: FittingType;
    quality?: ImageSharpeningQuality;
    devicePixelRatio?: number;
    focalPoint?: FocalPoint;
    crop?: Crop;
    upscaleMethod?: string;
    scrollEffect?: 'parallax' | 'fixed';
    hasAnimation?: boolean;
    userDomainMediaURL?: string;
  };
  sourceSets?: SourceSet[];
  hasAnimation?: boolean;
  encoding?: Encoding;
};

export type ImagePlaceholderProps = {
  /**
   * Utility for getting URI and CSS for rendering an image placeholder (LQIP)
   * @param {Object} data
   * @returns {Object}
   */
  getPlaceholder?: (data: ImagePlaceholderData) => ImageAttributesData;
  /**
   * Width of the target element the image will be rendered into
   */
  containerWidth?: number;
  /**
   * Height of the target element the image will be rendered into
   */
  containerHeight?: number;
};

export type BaseImageProps = ImagePlaceholderProps & {
  id?: string;
  /**
   * The id of component's parent
   * Necessary for background effects
   */
  containerId?: string;
  /**
   * Image uri
   */
  uri: string;
  /**
   * Image name
   */
  name?: string;
  /**
   * Original image width
   */
  width: number;
  /**
   * Original image height
   */
  height: number;
  /**
   * Image display mode
   */
  displayMode: FittingType;
  /**
   * Indicates whether image has a scroll effect (BackgroundReveal, BackgroundParallax, BackgroundParallaxZoom etc.)
   */
  hasBgScrollEffect?: 'true' | '';
  /**
   * Name of background scroll effect
   */
  bgEffectName?: string;
  /**
   * Holds image quality data
   */
  quality?: ImageSharpeningQuality;
};

export type ImagePlatformProps = BaseImageProps & {
  /**
   * Image alternate text
   */
  alt: string;
  /**
   * The pixel ratio of the device
   */
  devicePixelRatio?: number;
  /**
   * Alignment of the image
   */
  alignType?: AlignType;
  /**
   * Image focalPoint
   */
  focalPoint?: FocalPoint;
  /**
   * Image title text
   */
  title?: string;
  /**
   * Image crop
   */
  crop?: Crop;
  /**
   * Image upscale method
   */
  upscaleMethod?: UpscaleMethod;
};

export type FocalPoint = {
  x: number;
  y: number;
};

export type Crop = {
  x: number;
  y: number;
  width: number;
  height: number;
  svgId?: string;
  flip?: 'vertical' | 'horizontal' | 'both' | 'none';
  rotate?: number;
};

export type ImageProps = ImagePlatformProps & {
  targetWidth?: number;
  targetHeight?: number;
  targetScale?: number;
  className?: string;
  role?: string;
  crop?: any;
  bgEffectName?: string;
  upscaleMethod?: UpscaleMethod;
  imageStyles?: { [key: string]: string } | {};
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  shouldUseLQIP?: boolean;
  getPlaceholder?: (data: any) => any;
  containerWidth?: number;
  containerHeight?: number;
  isInFirstFold?: boolean;
  placeholderTransition?: PlaceholderTransition;
  socialAttrs?: SocialAttributes;
  isSEOBot?: boolean;
  skipMeasure?: boolean;
  filterEffectSvgString?: string;
  filterEffectSvgUrl?: string;
  hasAnimation?: boolean;
  encoding?: Encoding;
  isFluidLayout?: boolean;
  baseHostURL?: string;
};

export type WixImageExternalServices = {
  mutationService?: any;
  isExperimentOpen?: (experimentName: string) => boolean;
  getMediaDimensionsByEffect?: any;
  disableImagesLazyLoading?: boolean;
  getScreenHeightOverride?: () => number;
};

export type WixImageServices = WixImageExternalServices & {
  imageLoader: any;
  resizeService?: ResizeObserver;
  intersectionService?: IntersectionObserver;
};

export type WixImageMeasures = {
  width?: number;
  height?: number;
  screenHeight?: number;
  imgSrc?: string | null;
  boundingRect?: DOMRect;
  mediaHeightOverrideType?: 'viewport' | 'fixed' | '';
  sourceSetsTargetHeights?: { [key: string]: number };
  srcset?: string;
  top?: number;
  left?: number;
};

export type WixImageXMeasures = WixImageMeasures & {
  documentScroll: number;
};

export type WixImageDomNodes = Record<
  string,
  HTMLElement | HTMLImageElement | null
>;

export type SocialAttributes = {
  'data-pin-nopin'?: boolean | undefined;
  'data-pin-url'?: string;
  'data-pin-media'?: string;
};

export type ImagePresets = {
  [key: string]: {
    uri: string;
    width: number;
    height: number;
  };
};

export type EnvConsts = {
  disableImagesLazyLoading?: boolean;
  staticMediaUrl: string;
  mediaRootUrl: string;
  isViewerMode: boolean;
  experiments?: Record<string, string | boolean>;
  devicePixelRatio?: number;
  externalBaseUrl?: string;
  userDomainMediaPrefixes?: string[];
};

export type WowImage = Element & {
  reLayout: () => void;
  observeIntersect: () => void;
  unobserveIntersect: () => void;
  observeResize: () => void;
  unobserveResize: () => void;
};

// TODO(ameerabuf) - is this enough?
export type MediaSizeQuery = {
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
};

/* image's content data */
export type ImageMedia = {
  /* URI to fetch image from. for external content use a URL. */
  uri: string;
  /* Human-readable image filename. */
  name?: string;
  /* full width of original image in pixels. */
  width: number;
  /* full height of original image in pixels. */
  height: number;
  /* whether the image is of animated format (animated-WebP/AVIF/GIF). */
  animated?: boolean;
  /* crop data for the image. TODO(ameerabuf) - currently implemented in percentages, maybe use absolute pixxels? */
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  quality?: number;
  alt?: string;
};

/* data required for rendering the inner source HTML elements in ImageNg */
export type ImageSource = {
  /* image's content data */
  image: ImageMedia;
  /* data for media attribute to render. currently supports only min/max-width/height. */
  media?: MediaSizeQuery;
  /* data for sizes attribute to render. when the layout of the containing element is known, this data should
     reflect its width in the relevant breakpoints. */
  sizes: {
    /* the media query to test for using this size. relevant only if within the bounds of the outer media attribute
       there is some change in expected layout - if layout is similar, only a single size with no media should be provided. */
    media?: MediaSizeQuery;
    /* expected rendered width of the containing element in css length units. percentages are NOT supported. */
    size: UnitLength;
  }[];
  /* list of possible resolutions to fetch (given by the width in pixels). Use for network performance optimization.
     TODO(ameerabuf) - this modeling assumes that a targetSize together with ImageMedia is enough to generate
                       full URL of the image with the target resolution, which is false for external URLs. */
  targetSizes: number[];
};

/* props for ImageNg - the only-responsive image infra component. */
export type ImageNGProps = {
  /* id to render on root picture element. */
  id: string;
  /* alt to render on img element. ignored if decorative is set to true. */
  alt?: string;
  /* data for HTML source elements. Implements art-direction and responsiveness. assumes the sources are sorted
     according to their desired DOM-order (decided by the desired media logic -
     https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/picture#:~:text=%3Cpicture%3E.-,The%20media%20attribute,-The%20media%20attribute). */
  sources: ImageSource[];
  /* fetching priority. controls whether to use the browser's lazy-loading or high-priority fetching implementations.
     for best performance, set 'high' priority fir images above the fold and 'low' priority otherwise. */
  priority: 'high' | 'low';
  /* whether the image is purely decorative. if true, renders 'aria-hidden: true' empty alt string on img element. */
  decorative?: boolean;
  /* role to render on img element. */
  role?: 'img' | 'none' | 'presentation';
  /* dataset to render on root element. to use for any external mechanism implementation that requires rendering
     special data attributes. the keys are expected to be camelCased and are converted to kebab-case with 'data-'
     prefix when rendered. */
  dataset?: { [key: string]: string };
};
